<!-- Показывает список трекеров телематики и действия управления ими -->
<template>
  <div class="trackers-section">
    <div class="trackers-nav">
      <h3 class="trackers-title">Трекеры телематики</h3>
      <div class="trackers-actions">
        <button
          type="button"
          class="trackers-settings-btn"
          title="Часовой пояс телеметрии"
          aria-label="Часовой пояс телеметрии"
          @click="goToTelemetrySettings"
        >
          <AppIcon name="settings" :size="18" />
        </button>
        <base-primary-button
          class="nav-btn"
          @click="openCreateModal"
        >
          Добавить трекер
        </base-primary-button>
      </div>
    </div>

    <base-loader v-if="dataIsLoading" />

    <template v-else>
      <div v-if="trackers.length === 0" class="no-data">
        Трекеры не добавлены
      </div>

      <ul v-else class="trackers-list">
        <BaseSettingsListItemCard
          v-for="tracker in localTrackers"
          :key="tracker.id"
          :enabled="tracker.enabled"
          :is-loading="isLoading"
          :show-edit="false"
          :show-delete="false"
          @toggle="toggleTracker(tracker)"
        >
          <template #actions>
            <button
              v-if="!tracker.primary"
              class="tracker-card-action-btn tracker-card-action-btn--primary"
              type="button"
              :disabled="isLoading"
              title="Назначить основным"
              aria-label="Назначить основным"
              @click="confirmSetPrimary(tracker)"
            >
              <AppIcon name="star" :size="20" />
            </button>

            <button
              class="tracker-card-action-btn tracker-card-action-btn--sensors"
              type="button"
              title="Датчики трекера"
              aria-label="Датчики трекера"
              @click="goToSensors(tracker)"
            >
              <AppIcon name="pulse" :size="20" />
            </button>

            <button
              class="tracker-card-action-btn tracker-card-action-btn--edit"
              type="button"
              title="Редактировать трекер"
              aria-label="Редактировать трекер"
              @click="startEdit(tracker)"
            >
              <AppIcon name="edit" :size="20" />
            </button>
          </template>

          <div class="tracker-top">
            <span class="tracker-name">
              {{ tracker.name || `Трекер #${tracker.id}` }}
            </span>

            <span v-if="tracker.primary" class="badge badge--primary">
              <AppIcon name="star" :size="11" :filled="true" />
              Основной
            </span>

            <span
              v-if="tracker.skip_protocol_password"
              class="badge badge--warning"
              title="Проверка пароля отключена"
            >
              без проверки пароля
            </span>
          </div>

          <div class="tracker-field">
            <span class="tracker-field-label">Транспорт:</span>
            <template v-if="vehicleNames[tracker.vehicle_id]">
              <span class="tracker-field-value vehicle-name">
                {{ vehicleNames[tracker.vehicle_id].displayName }}
              </span>
              <!-- Госномер транспорта, привязанного к трекеру -->
              <VehicleRegNumberBadge
                v-if="
                  vehicleNames[tracker.vehicle_id].registrationNumber
                "
                :number="
                  vehicleNames[tracker.vehicle_id].registrationNumber
                "
                size="sm"
              />
            </template>
            <span
              v-else-if="tracker.vehicle_id"
              class="tracker-field-value"
            >
              #{{ tracker.vehicle_id }}
            </span>
            <span v-else class="tracker-field-value">—</span>
          </div>

          <div class="tracker-field">
            <span class="tracker-field-label">IMEI:</span>
            <span class="tracker-field-value">
              {{ tracker.imei || "—" }}
            </span>
          </div>
        </BaseSettingsListItemCard>
      </ul>
    </template>

    <TrackerCreateModal
      :visible="showCreateModal"
      :is-loading="isLoading"
      @save="handleCreate"
      @cancel="closeCreateModal"
    />

    <TrackerEditModal
      :visible="editingTracker !== null"
      :tracker="editingTracker"
      :vehicle-info="
        editingTracker
          ? vehicleNames[editingTracker.vehicle_id] || null
          : null
      "
      :is-loading="isLoading"
      @save="(payload) => handleUpdate(editingTracker.id, payload)"
      @cancel="closeEditDialog"
    />

    <!-- Диалог подтверждения смены основного трекера -->
    <base-dialog
      :visible="showSetPrimaryDialog"
      title="Сменить основной трекер?"
      :actions="setPrimaryDialogActions"
      :close-on-overlay="false"
      @action="handleSetPrimaryDialogAction"
    >
      <p class="dialog-text">
        Основным станет трекер
        <strong>
          {{
            pendingPrimaryTracker?.name ||
            `#${pendingPrimaryTracker?.id}`
          }}
        </strong>
        .
      </p>
      <p class="dialog-text dialog-text--mt">
        Все данные телематики будут поступать с него. Это действие
        затрагивает маршруты, отчёты и мониторинг в реальном времени.
        Продолжить?
      </p>
    </base-dialog>
  </div>
</template>

<script>
import { mapState, mapActions } from "pinia";

import { useTelemetryStore } from "@/stores/telemetryStore";
import { useVehicleStore } from "@/stores/vehicleStore";
import { useNotificationStore } from "@/stores/notificationStore";

import { getCompanyId } from "@/utils/userUtils";

import vehiclesApi from "@/services/api/vehiclesApi";
import telemetryTrackersApi from "@/services/api/telemetry/telemetryTrackersApi";

import AppIcon from "@/components/Icon/AppIcon.vue";
import TrackerCreateModal from "@/components/Telemetry/TrackerCreateModal.vue";
import TrackerEditModal from "@/components/Telemetry/TrackerEditModal.vue";
import VehicleRegNumberBadge from "@/components/Vehicle/VehicleRegNumberBadge.vue";

export default {
  name: "TelemetryTrackersSection",

  components: {
    AppIcon,
    TrackerCreateModal,
    TrackerEditModal,
    VehicleRegNumberBadge,
  },

  data() {
    return {
      dataIsLoading: true,
      showCreateModal: false,
      editingTrackerId: null,
      localTrackers: [],
      vehicleNames: {},
      companyId: getCompanyId(),
      showSetPrimaryDialog: false,
      pendingPrimaryTracker: null,
      setPrimaryDialogActions: [
        { key: "confirm", label: "Да, сменить", type: "primary" },
        { key: "close", label: "Отмена", type: "secondary" },
      ],
    };
  },

  computed: {
    ...mapState(useTelemetryStore, ["trackers", "isLoading"]),

    notificationStore() {
      return useNotificationStore();
    },

    vehicleStore() {
      return useVehicleStore();
    },

    editingTracker() {
      return (
        this.localTrackers.find(
          (tracker) => tracker.id === this.editingTrackerId
        ) || null
      );
    },
  },

  methods: {
    ...mapActions(useTelemetryStore, [
      "getTrackersRequest",
      "createTracker",
      "updateTracker",
      "trackerEnable",
      "trackerDisable",
    ]),

    goToSensors(tracker) {
      this.$router.push({
        name: "TrackerSensors",
        params: { trackerId: tracker.id },
      });
    },

    goToTelemetrySettings() {
      this.$router.push({ name: "TelemetrySettings" });
    },

    async loadTrackers() {
      try {
        this.dataIsLoading = true;
        await this.getTrackersRequest();
        this.localTrackers = [...this.trackers];
        await this.loadVehicleNames();
      } finally {
        this.dataIsLoading = false;
      }
    },

    async loadVehicleNames() {
      const ids = [
        ...new Set(
          this.localTrackers.map((t) => t.vehicle_id).filter(Boolean)
        ),
      ];
      await Promise.all(ids.map((id) => this.fetchVehicleName(id)));
    },

    async fetchVehicleName(vehicleId) {
      if (this.vehicleNames[vehicleId]) return;

      try {
        const response = await vehiclesApi.show(
          this.companyId,
          vehicleId
        );
        const v = response.data.result;

        const nameParts = [v.manufacturer, v.model]
          .filter(Boolean)
          .join(" ")
          .trim();

        this.vehicleNames = {
          ...this.vehicleNames,
          [vehicleId]: {
            displayName:
              nameParts || v.full_name || `Транспорт #${v.id}`,
            registrationNumber: v.registration_number,
            vehicleTypeLabel: this.vehicleStore.formatVehicleType(
              v.vehicle_type
            ),
          },
        };
      } catch {
        // покажем #id
      }
    },

    updateLocalTracker(updatedTracker) {
      const target = this.localTrackers.find(
        (t) => t.id === updatedTracker.id
      );
      if (target) {
        Object.keys(target).forEach((key) => delete target[key]);
        Object.assign(target, updatedTracker);
      }
    },

    openCreateModal() {
      this.showCreateModal = true;
      this.editingTrackerId = null;
    },

    closeCreateModal() {
      this.showCreateModal = false;
    },

    startEdit(tracker) {
      this.editingTrackerId = tracker.id;
      this.showCreateModal = false;
    },

    closeEditDialog() {
      this.editingTrackerId = null;
    },

    async handleCreate(payload) {
      try {
        await this.createTracker(payload);
        this.localTrackers = [...this.trackers];
        if (payload.vehicle_id) {
          await this.fetchVehicleName(payload.vehicle_id);
        }
        this.closeCreateModal();
        this.notificationStore.showSuccess("Трекер добавлен");
      } catch {
        // ошибка обрабатывается в сторе
      }
    },

    async handleUpdate(id, payload) {
      try {
        await this.updateTracker(id, payload);
        this.localTrackers = [...this.trackers];
        if (payload.vehicle_id) {
          await this.fetchVehicleName(payload.vehicle_id);
        }
        this.closeEditDialog();
        this.notificationStore.showSuccess("Трекер обновлён");
      } catch {
        // ошибка обрабатывается в сторе
      }
    },

    async toggleTracker(tracker) {
      try {
        if (tracker.enabled) {
          await this.trackerDisable(tracker.id);
          this.updateLocalTracker({ ...tracker, enabled: false });
          this.notificationStore.showSuccess("Трекер отключён");
        } else {
          await this.trackerEnable(tracker.id);
          this.updateLocalTracker({ ...tracker, enabled: true });
          this.notificationStore.showSuccess("Трекер включён");
        }
      } catch {
        // ошибка обрабатывается в сторе
      }
    },

    confirmSetPrimary(tracker) {
      this.pendingPrimaryTracker = tracker;
      this.showSetPrimaryDialog = true;
    },

    async handleSetPrimaryDialogAction(key) {
      this.showSetPrimaryDialog = false;

      if (key !== "confirm") {
        this.pendingPrimaryTracker = null;
        return;
      }

      try {
        await telemetryTrackersApi.setPrimary(
          this.companyId,
          this.pendingPrimaryTracker.id
        );
        await this.getTrackersRequest();
        this.localTrackers = [...this.trackers];
        this.notificationStore.showSuccess("Основной трекер изменён");
      } catch {
        this.notificationStore.showError(
          "Не удалось изменить основной трекер"
        );
      } finally {
        this.pendingPrimaryTracker = null;
      }
    },
  },

  async created() {
    await this.loadTrackers();
  },
};
</script>

<style scoped>
.trackers-section {
  margin-top: var(--base-out-indent);
}

.trackers-nav {
  display: flex;
  align-items: center;
  margin-bottom: var(--base-out-indent);
  gap: 12px;
}

.trackers-title {
  margin-right: auto;
}

.trackers-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.trackers-settings-btn {
  width: 34px;
  height: 34px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  background: #fff;
  color: #6b7280;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background-color 0.2s ease, color 0.2s ease,
    border-color 0.2s ease;
}

.trackers-settings-btn:hover {
  background: #f9fafb;
  color: var(--color-dark);
  border-color: var(--color-dark);
}

.trackers-list {
  display: flex;
  flex-direction: column;
  gap: var(--base-out-indent);
}

.tracker-top {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 10px;
}

.tracker-name {
  font-weight: 600;
  font-size: 17px;
}

.badge {
  font-size: 14px;
  font-weight: 600;
  padding: 5px 8px;
  border-radius: 10px;
}

.badge--primary {
  background-color: #e0f2fe;
  color: #0369a1;
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.badge--warning {
  background-color: #fff7ed;
  color: #c2410c;
}

.tracker-field {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  flex-wrap: wrap;
}

.tracker-card-action-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 35px;
  height: 35px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  color: #6b7280;
  background: none;
  transition: background-color 0.2s ease, color 0.2s ease;
}

.tracker-card-action-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.tracker-card-action-btn--primary:hover:not(:disabled) {
  background: #fef9c3;
  color: #b45309;
}

.tracker-card-action-btn--sensors:hover {
  background: #ccfbf1;
  color: var(--color-green);
}

.tracker-card-action-btn--edit:hover {
  background: #dbeafe;
  color: var(--color-blue);
}

.tracker-field-label {
  color: #666;
}

.tracker-field-value {
  color: var(--color-dark);
  font-family: monospace;
}

.vehicle-name {
  font-family: inherit;
}

.dialog-text {
  font-size: 14px;
  color: #4b5563;
  line-height: 1.6;
  max-width: none;
}

.dialog-text--mt {
  margin-top: 10px;
}
</style>
